import React, { useMemo, useState } from 'react';
import AutocompleteInput from './AutocompleteInput';
import { Declaration, DeclarationFilterField, DeclarationFilters } from '../../types/declaration';
import { getEmptyDeclarationFilters, getFilterFieldValues } from '../../utils/declarationUtils';
import './CertificatesFilter.css';

const emptyInputValues = {
  reg_number: '',
  product_kind: '',
  applicant_name: '',
  applicant_inn: '',
  manufacturer_full_name: '',
  manufacturer_inn: '',
  lab_names: ''
};

interface DeclarationsFilterProps {
  declarations: Declaration[];
  filters: DeclarationFilters;
  onFilterChange: (filters: DeclarationFilters) => void;
}

const DeclarationsFilter: React.FC<DeclarationsFilterProps> = ({ declarations, filters, onFilterChange }) => {
  const [inputValues, setInputValues] = useState(emptyInputValues);

  const uniqueValues = useMemo(() => {
    const getUniqueSorted = (field: DeclarationFilterField) => {
      const values = new Set<string>();
      declarations.forEach(declaration => {
        getFilterFieldValues(declaration, field).forEach(value => values.add(value));
      });
      return Array.from(values).sort();
    };
    return {
      reg_number: getUniqueSorted('reg_number'),
      product_kind: getUniqueSorted('product_kind'),
      applicant_name: getUniqueSorted('applicant_name'),
      applicant_inn: getUniqueSorted('applicant_inn'),
      manufacturer_full_name: getUniqueSorted('manufacturer_full_name'),
      manufacturer_inn: getUniqueSorted('manufacturer_inn'),
      lab_names: getUniqueSorted('lab_names')
    };
  }, [declarations]);

  const getDateRange = (field: 'reg_date' | 'end_date') => {
    const dates = declarations
      .map(declaration => declaration[field])
      .filter(date => !!date)
      .sort() as string[];
    return {
      minDate: dates.length > 0 ? dates[0] : '',
      maxDate: dates.length > 0 ? dates[dates.length - 1] : ''
    };
  };

  const handleInputChange = (field: DeclarationFilterField, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFilter = (field: DeclarationFilterField, value: string) => {
    if (value && !filters.fields[field].includes(value)) {
      onFilterChange({
        ...filters,
        fields: { ...filters.fields, [field]: [...filters.fields[field], value] }
      });
      setInputValues(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRemoveFilter = (field: DeclarationFilterField, value: string) => {
    onFilterChange({
      ...filters,
      fields: { ...filters.fields, [field]: filters.fields[field].filter(item => item !== value) }
    });
  };

  const handleChartFilterRemove = (type: 'statuses' | 'years', value: string) => {
    onFilterChange({
      ...filters,
      [type]: filters[type].filter(item => item !== value)
    });
  };

  const handleDateRangeChange = (field: 'regDate' | 'endDate', startDate: string, endDate: string) => {
    onFilterChange({
      ...filters,
      [field]: { start: startDate, end: endDate }
    });
  };

  const resetFilters = () => {
    setInputValues(emptyInputValues);
    onFilterChange(getEmptyDeclarationFilters());
  };

  const fieldFiltersCount = Object.values(filters.fields).reduce((acc, values) => acc + values.length, 0);
  const activeFiltersCount = fieldFiltersCount +
    filters.statuses.length +
    filters.years.length +
    (filters.regDate.start || filters.regDate.end ? 1 : 0) +
    (filters.endDate.start || filters.endDate.end ? 1 : 0);

  const getFilterLabel = (field: DeclarationFilterField) => {
    switch (field) {
      case 'reg_number':
        return 'Номер';
      case 'product_kind':
        return 'Вид';
      case 'applicant_name':
      case 'manufacturer_full_name':
        return 'Название';
      case 'applicant_inn':
      case 'manufacturer_inn':
        return 'ИНН';
      case 'lab_names':
        return 'Лаборатория';
      default:
        return '';
    }
  };

  const renderMultiSelect = (name: DeclarationFilterField, label: string) => {
    const options = uniqueValues[name];
    return (
      <div className="filter-field">
        <label htmlFor={`declaration-${name}`}>{label}</label>
        <div className="multi-select-container">
          <AutocompleteInput
            id={`declaration-${name}`}
            value={inputValues[name]}
            options={options}
            selectedValues={filters.fields[name]}
            placeholder={`Выберите (${options.length})`}
            onChange={(value) => handleInputChange(name, value)}
            onSelectOption={(value) => handleAddFilter(name, value)}
          />
        </div>
      </div>
    );
  };

  const renderDateRangeFilter = (field: 'regDate' | 'endDate', dateField: 'reg_date' | 'end_date', label: string) => {
    const currentRange = filters[field];
    const { minDate, maxDate } = getDateRange(dateField);

    return (
      <div className="date-range-filter">
        <h5>{label}</h5>
        <div className="date-inputs">
          <div className="date-input-group">
            <label htmlFor={`${field}-start`}>Начало периода:</label>
            <input
              id={`${field}-start`}
              type="date"
              value={currentRange.start}
              onChange={(e) => handleDateRangeChange(field, e.target.value, currentRange.end)}
              min={minDate}
              max={maxDate}
              className="date-input"
            />
          </div>
          <div className="date-input-group">
            <label htmlFor={`${field}-end`}>Конец периода:</label>
            <input
              id={`${field}-end`}
              type="date"
              value={currentRange.end}
              onChange={(e) => handleDateRangeChange(field, currentRange.start, e.target.value)}
              min={minDate}
              max={maxDate}
              className="date-input"
            />
          </div>
          <button
            type="button"
            onClick={() => handleDateRangeChange(field, '', '')}
            className="clear-dates-btn"
            title="Очистить даты"
          >
            Сбросить
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="certificates-filter">
      <div className="filter-header">
        <h3>Фильтры {activeFiltersCount > 0 && <span className="filter-count">({activeFiltersCount})</span>}</h3>
        <button className="reset-button" onClick={resetFilters}>Сбросить</button>
      </div>

      {(fieldFiltersCount > 0 || filters.statuses.length > 0 || filters.years.length > 0) && (
        <div className="active-filters">
          <h4>Активные фильтры:</h4>
          {Object.entries(filters.fields).map(([field, values]) => {
            return (values as string[]).map(value => (
              <div key={`${field}-${value}`} className="active-filter-item">
                <span data-label={getFilterLabel(field as DeclarationFilterField)}>{value}</span>
                <button
                  className="remove-filter-button"
                  onClick={() => handleRemoveFilter(field as DeclarationFilterField, value)}
                >
                  ×
                </button>
              </div>
            ));
          })}
          {filters.statuses.map(status => (
            <div key={status} className="active-filter-item">
              <span data-label="Статус">Статус: {status}</span>
              <button
                className="remove-filter-button"
                onClick={() => handleChartFilterRemove('statuses', status)}
              >
                ×
              </button>
            </div>
          ))}
          {filters.years.map(year => (
            <div key={year} className="active-filter-item">
              <span data-label="Год">Год: {year}</span>
              <button
                className="remove-filter-button"
                onClick={() => handleChartFilterRemove('years', year)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="filter-fields">
        <div className="filter-group2">
          <h4>Декларация</h4>
          {renderMultiSelect('reg_number', 'Номер декларации')}
          {renderMultiSelect('product_kind', 'Вид продукции')}
        </div>

        <div className="filter-group2">
          <h4>Заявитель</h4>
          {renderMultiSelect('applicant_name', 'Название')}
          {renderMultiSelect('applicant_inn', 'ИНН')}
        </div>

        <div className="filter-group2">
          <h4>Изготовитель</h4>
          {renderMultiSelect('manufacturer_full_name', 'Полное название')}
          {renderMultiSelect('manufacturer_inn', 'ИНН')}
        </div>

        <div className="filter-group2">
          <h4>Лаборатории</h4>
          {renderMultiSelect('lab_names', 'Название лаборатории')}
        </div>

        <div className="filter-group2">
          <h4>Даты</h4>
          {renderDateRangeFilter('regDate', 'reg_date', 'Дата регистрации декларации')}
          {renderDateRangeFilter('endDate', 'end_date', 'Дата окончания декларации')}
        </div>
      </div>
    </div>
  );
};

export default DeclarationsFilter;
